/**
 * adapters/lucideAdapter.jsx
 * Backward-compatibility adapters allowing Homy bespoke icons to drop in
 * seamlessly anywhere existing code expects a Lucide icon component (accepting size, className, etc.).
 */

import * as React from "react";
import { HomyIcon } from "../HomyIcon.jsx";

/**
 * Creates a Lucide-compatible component for a given domain entity and optional action.
 * @param {string} domainName - Canonical domain key (e.g. "properties", "support")
 * @param {string|null} [action=null] - Optional action modifier (e.g. "add", "edit")
 * @returns {React.ForwardRefExoticComponent<any>}
 */
export function getDomainIcon(domainName, action = null) {
  const Component = React.forwardRef(
    ({ size = 20, className = "", color, ...props }, ref) => {
      return (
        <HomyIcon
          ref={ref}
          name={domainName}
          action={action}
          size={size}
          color={color}
          className={className}
          {...props}
        />
      );
    }
  );
  Component.displayName = `DomainIcon_${domainName}${action ? `_${action}` : ""}`;
  return Component;
}

/**
 * Creates a generic Lucide-compatible component for any registered glyph name.
 * @param {string} iconName - Canonical icon key
 * @param {object} [defaultProps={}] - Default props to pass to HomyIcon
 * @returns {React.ForwardRefExoticComponent<any>}
 */
export function createLucideAdapter(iconName, defaultProps = {}) {
  const Component = React.forwardRef(
    ({ size = 20, className = "", ...props }, ref) => {
      return (
        <HomyIcon
          ref={ref}
          name={iconName}
          size={size}
          className={className}
          {...defaultProps}
          {...props}
        />
      );
    }
  );
  Component.displayName = `LucideAdapter_${iconName}`;
  return Component;
}

/**
 * Creates a Composite Action Icon component.
 * @param {string} domainName - Canonical domain key
 * @param {string} action - Action modifier (e.g. "add", "schedule", "edit")
 * @param {object} [defaultProps={}]
 * @returns {React.ForwardRefExoticComponent<any>}
 */
export function createCompositeIcon(domainName, action = "add", defaultProps = {}) {
  const Component = React.forwardRef(
    ({ size = 20, className = "", ...props }, ref) => {
      return (
        <HomyIcon
          ref={ref}
          name={domainName}
          action={action}
          size={size}
          className={className}
          {...defaultProps}
          {...props}
        />
      );
    }
  );
  Component.displayName = `Composite_${domainName}_${action}`;
  return Component;
}

// Canonical drop-in composite action icons (All 17 domain factories + aliases)
export const AddPropertyIcon = createCompositeIcon("properties", "add");
export const NewMaintenanceIcon = createCompositeIcon("maintenance", "add");
export const CreateTaskIcon = createCompositeIcon("tasks", "add");
export const NewSupportTicketIcon = createCompositeIcon("support", "add");
export const ScheduleVisitIcon = createCompositeIcon("calendar", "schedule");
export const NewCalendarEventIcon = ScheduleVisitIcon;
export const DraftLeaseIcon = createCompositeIcon("rentals", "add");
export const CreateRentalIcon = DraftLeaseIcon;
export const RecordPaymentIcon = createCompositeIcon("payments", "add");
export const CreateListingIcon = createCompositeIcon("listings", "add");
export const AddVendorIcon = createCompositeIcon("maintenance", "add");
export const NewProjectIcon = createCompositeIcon("maintenance", "add");
export const AddMemberIcon = createCompositeIcon("organizations", "add");
export const AddOrganizationIcon = AddMemberIcon;
export const CreateTemplateIcon = createCompositeIcon("homysign", "add");
export const CreateApplicationIcon = createCompositeIcon("applications", "add");
export const CreateDatabaseIcon = createCompositeIcon("databases", "add");
export const CreateMarketplaceIcon = createCompositeIcon("marketplace", "add");
export const UpgradeMembershipIcon = createCompositeIcon("memberships", "upgrade");
export const NewCommentIcon = createCompositeIcon("comments", "add");
export const SendInboxMessageIcon = createCompositeIcon("inbox", "add");
export const AddContactIcon = createCompositeIcon("core", "add");
